import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader, Check, AlertTriangle, Sparkles } from 'lucide-react';
import { API_URL } from '../../config';

/**
 * Snapshot-3.0-Final-Step:
 * - Sendet POST /api/avatar/create mit den im PersonalDataStep validierten photo_ids
 * - Zeigt einen kurzen Erfolgs-Screen
 * - Setzt localStorage `pending_avatar_id` (Dashboard-Banner übernimmt das Polling)
 * - Redirected nach kurzer Pause zurück ins Dashboard
 */
const TrainingAndGenerator = ({ wizardData, onBackToDashboard, onBackToPhotos }) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('submitting'); // submitting | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const submittedRef = useRef(false); // verhindert Doppel-Submit (StrictMode / Re-Mount)

  useEffect(() => {
    if (submittedRef.current) return;
    submittedRef.current = true;

    const submit = async () => {
      try {
        const slots = wizardData?.slots || {};
        const photo_ids = Object.fromEntries(
          Object.entries(slots).map(([slot, v]) => [slot, v.photoId]),
        );

        const res = await fetch(`${API_URL}/api/avatar/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('snapshot_token')}`,
          },
          body: JSON.stringify({
            name: wizardData.name,
            avatar_type: wizardData.avatarType,
            photo_ids,
          }),
        });

        if (!res.ok) {
          const txt = await res.text();
          let detail = txt;
          try { detail = JSON.parse(txt).detail || txt; } catch (_) {}
          throw new Error(detail || `HTTP ${res.status}`);
        }

        const data = await res.json();

        // Dashboard-Banner übernimmt das Polling
        if (data.avatar_id) {
          localStorage.setItem('pending_avatar_id', data.avatar_id);
          // Markiere "erster Avatar gerade gestartet" für SNAP-Agent (Tour)
          localStorage.setItem('training_just_started', 'true');
          if (wizardData.name) localStorage.setItem('training_user_name', wizardData.name);
        }

        // Wizard-Daten aufräumen (alte Persistenz)
        localStorage.removeItem('snapshot_wizard_data');
        localStorage.removeItem('wizard_current_step');

        setPhase('success');

        // Auto-Redirect ins Dashboard nach 1.8 s
        setTimeout(() => {
          if (onBackToDashboard) onBackToDashboard();
          else navigate('/');
        }, 1800);
      } catch (err) {
        console.error('Avatar-Create failed:', err);
        setErrorMessage(err.message || 'Unbekannter Fehler');
        setPhase('error');
      }
    };

    submit();
  }, []); // eslint-disable-line

  return (
    <div className="text-white min-h-[calc(100vh_-_6rem)] flex items-center justify-center px-4">
      <div className="max-w-xl w-full">
        <div className="w-full">
          <div className="bg-black border-2 border-[#10B981]/20 rounded-2xl p-8 sm:p-12 glow-green-soft text-center">
            {phase === 'submitting' && (
              <>
                <Loader className="w-14 h-14 text-[#10B981] animate-spin mx-auto mb-6" />
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                  Wir bereiten deinen Avatar vor ...
                </h1>
                <p className="text-white/60">Einen Moment bitte.</p>
              </>
            )}

            {phase === 'success' && (
              <>
                <div className="w-16 h-16 rounded-full bg-[#10B981] text-black flex items-center justify-center mx-auto mb-6 glow-green">
                  <Check className="w-9 h-9" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[#10B981] mb-3">
                  Dein Avatar wird vorbereitet
                </h1>
                <p className="text-white/80 mb-2">
                  Nur noch ein bisschen Geduld, dann kannst du mit dem Erstellen von Bildern und Videos beginnen.
                </p>
                <p className="text-white/50 text-sm flex items-center justify-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#10B981]" />
                  Wir leiten dich automatisch ins Dashboard ...
                </p>
              </>
            )}

            {phase === 'error' && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-500/20 border-2 border-red-500 text-red-400 flex items-center justify-center mx-auto mb-6">
                  <AlertTriangle className="w-9 h-9" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-red-400 mb-3">
                  Avatar-Erstellung fehlgeschlagen
                </h1>
                <p className="text-white/70 text-sm mb-2 break-words">
                  {errorMessage}
                </p>
                <p className="text-white/50 text-sm mb-6">
                  Keine Sorge — deine Eingaben bleiben erhalten. Du kannst die Fotos anpassen und es direkt nochmal versuchen.
                </p>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => onBackToPhotos ? onBackToPhotos(errorMessage) : (onBackToDashboard ? onBackToDashboard() : navigate('/'))}
                    className="px-6 py-3 rounded-xl font-bold bg-gradient-to-br from-[#10B981] to-[#059669] text-black glow-green hover:opacity-90 transition-all"
                  >
                    Zurück zu den Fotos
                  </button>
                  <button
                    onClick={() => onBackToDashboard ? onBackToDashboard() : navigate('/')}
                    className="text-white/40 hover:text-white/70 text-sm transition-colors"
                  >
                    Abbrechen und zum Dashboard
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingAndGenerator;
